import React, {useEffect, useState} from 'react';
import {Box, CircularProgress, IconButton, Typography} from "@mui/joy";
import Dialog from "@mui/material/Dialog";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/system/useMediaQuery";
import {useTheme} from "@mui/joy";
import {useTranslation} from "react-i18next";
import {OpenInFullRounded} from "@mui-symbols-material/w300";
import {BasicModalContent} from "@dreebit-registry/vsm-microservice-components";

// mermaid is ~2 MB minified — loaded lazily as its own chunk, only when a
// report actually contains a diagram. The main bundle stays untouched.
let mermaidPromise: Promise<any> | null = null;
const loadMermaid = () => {
    if (!mermaidPromise) {
        mermaidPromise = import('mermaid').then((module) => {
            const mermaid = module.default;
            mermaid.initialize({
                startOnLoad: false,
                // strict sanitizes the AI-generated diagram source (no scripts,
                // no click handlers) before we inject the resulting SVG.
                securityLevel: 'strict',
                theme: 'neutral',
                fontFamily: 'inherit',
            });
            return mermaid;
        });
    }
    return mermaidPromise;
};

let renderSeq = 0;

/**
 * Fullscreen-ish dialog showing the diagram at its NATURAL size. Mermaid
 * emits width="100%" plus an inline max-width, which scales wide diagrams
 * down to the container — fine inline, but here the effect pins explicit
 * pixel dimensions from the viewBox so the diagram is readable and the
 * content box scrolls both axes.
 */
const MermaidZoomDialog = ({svg, onClose}: {svg: string; onClose: () => void}) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));

    // Callback ref instead of useEffect: MUI's Dialog portal attaches the
    // content on a later render pass, after a mount effect would already
    // have run against an empty ref.
    const pinNaturalSize = (el: HTMLDivElement | null) => {
        const svgEl = el?.querySelector('svg');
        if (!svgEl) return;
        const viewBox = (svgEl as SVGSVGElement).viewBox?.baseVal;
        if (viewBox?.width) {
            svgEl.setAttribute('width', `${Math.ceil(viewBox.width)}px`);
            if (viewBox.height) svgEl.setAttribute('height', `${Math.ceil(viewBox.height)}px`);
            (svgEl as SVGSVGElement).style.maxWidth = 'none';
        }
    };

    return (
        <Dialog
            open
            onClose={onClose}
            fullScreen={isFullScreen}
            maxWidth={false}
            PaperProps={{sx: isFullScreen ? {} : {width: '92vw', height: '90vh'}}}
        >
            <BasicModalContent
                onClose={onClose}
                title={<Typography level={"titleSmallLight"}>{t('Diagram')}</Typography>}
                style={{display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0}}
            >
                <Box
                    ref={pinNaturalSize}
                    data-testid="mermaid-zoom-content"
                    sx={{flex: 1, minHeight: 0, overflow: 'auto', p: '8px 16px 16px 16px'}}
                    dangerouslySetInnerHTML={{__html: svg}}
                />
            </BasicModalContent>
        </Dialog>
    );
};

interface Props {
    chart: string;
    /** Rendered when the diagram source does not parse. */
    fallback: React.ReactNode;
}

const MermaidDiagram = ({chart, fallback}: Props) => {
    const {t} = useTranslation();
    const [svg, setSvg] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);
    const [zoomOpen, setZoomOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setSvg(null);
        setFailed(false);
        loadMermaid()
            .then((mermaid) => mermaid.render(`risk-report-mermaid-${++renderSeq}`, chart))
            .then((result: {svg: string}) => {
                if (!cancelled) setSvg(result.svg);
            })
            .catch(() => {
                if (!cancelled) setFailed(true);
            });
        return () => {
            cancelled = true;
        };
    }, [chart]);

    if (failed) return <>{fallback}</>;

    if (!svg) {
        return (
            <Box sx={{py: '16px', display: 'flex', justifyContent: 'center'}}>
                <CircularProgress variant="soft" size="sm"/>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                position: 'relative',
                mb: '10px',
                // Inline view stays fit-to-width (mermaid's own scaling); the
                // expand button opens the readable natural-size dialog.
                '& .mermaid-expand': {opacity: 0, transition: 'opacity 120ms'},
                '&:hover .mermaid-expand, & .mermaid-expand:focus-visible': {opacity: 1},
            }}
        >
            <Box
                sx={{overflowX: 'auto', '& svg': {maxWidth: '100%', height: 'auto'}}}
                // SVG produced and sanitized by mermaid (securityLevel: strict).
                dangerouslySetInnerHTML={{__html: svg}}
            />
            <Tooltip title={t('Show full size')}>
                <IconButton
                    className="mermaid-expand"
                    size="sm"
                    variant="soft"
                    aria-label={t('Show full size')}
                    onClick={() => setZoomOpen(true)}
                    sx={{position: 'absolute', top: '4px', right: '4px', backgroundColor: 'background.paper', boxShadow: 'sm'}}
                    data-testid="mermaid-expand"
                >
                    <OpenInFullRounded fontSize="small"/>
                </IconButton>
            </Tooltip>
            {zoomOpen && <MermaidZoomDialog svg={svg} onClose={() => setZoomOpen(false)}/>}
        </Box>
    );
};

export default MermaidDiagram;
